import { useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import { useMutation } from '@tanstack/react-query';

type BugReportModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function BugReportModal({ isOpen, onClose }: BugReportModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!description.trim()) {
        throw new Error('Descrição é obrigatória');
      }

      let screenshotKey: string | undefined;

      if (screenshot) {
        const presignPayload = {
          fileName: screenshot.name,
          mimeType: screenshot.type,
        };
        const presignResponse = await api.post('/documents/presign', presignPayload);
        const presignData = presignResponse.data?.data ?? presignResponse.data;
        const { url, key } = presignData;
        if (!url || !key) {
          throw new Error('Resposta do servidor inválida ao gerar URL de upload.');
        }

        const putRes = await fetch(url, {
          method: 'PUT',
          body: screenshot,
          headers: { 'Content-Type': screenshot.type || 'application/octet-stream' },
        });
        if (!putRes.ok) {
          throw new Error(`Upload da imagem falhou: ${putRes.status}`);
        }

        const confirmPayload = {
          key,
          fileName: screenshot.name,
          mimeType: screenshot.type,
          size: screenshot.size,
        };
        const confirmResponse = await api.post('/documents/confirm', confirmPayload);
        const confirmData = confirmResponse.data?.data ?? confirmResponse.data;
        screenshotKey = confirmData?.s3Key || confirmData?.key || key;
      }

      await api.post('/bug-reports', {
        title: title.trim() || 'Bug report',
        description: description.trim(),
        screenshotKey,
      });
    },
    onSuccess: () => {
      setTitle('');
      setDescription('');
      setScreenshot(null);
      setError(null);
      onClose();
    },
    onError: (err: any) => {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Não foi possível enviar o bug report.';
      setError(Array.isArray(message) ? message.join(', ') : message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  if (!isOpen || typeof document === 'undefined') return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bug-report-modal-title"
    >
      <div
        className="flex w-full max-w-md max-h-[calc(100vh-2rem)] flex-col overflow-hidden bg-white dark:bg-[#1a1a1a]"
        style={{
          fontFamily: 'Poppins, sans-serif',
          borderRadius: 15,
          boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex flex-shrink-0 items-center justify-between p-5 pb-4"
          style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}
        >
          <h2
            id="bug-report-modal-title"
            className="text-[16px] text-[#06141B] dark:text-[#F5F6F5]"
            style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}
          >
            Reportar um problema
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#06141B] text-[#F5F6F5] transition-opacity hover:opacity-80 disabled:opacity-50 dark:bg-[#F5F6F5] dark:text-[#06141B]"
            aria-label="Fechar"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            <p
              className="mb-4 text-[#06141B] dark:text-[#F5F6F5]"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: 14, lineHeight: 1.5 }}
            >
              Descreva o que aconteceu para que nossa equipe possa investigar e resolver o mais rápido possível.
            </p>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="bug-title"
                  className="mb-2 block text-[#06141B]/60 dark:text-[#F5F6F5]/60"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, fontWeight: 500 }}
                >
                  Título (opcional)
                </label>
                <input
                  id="bug-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-[10px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#06141B]/20 dark:bg-[#111] dark:text-[#F5F6F5] dark:focus:ring-[#F5F6F5]/20"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 12,
                    color: '#06141B',
                    border: '1px solid rgba(6, 20, 27, 0.15)',
                  }}
                  placeholder="Ex: Erro ao salvar tarefa"
                  disabled={mutation.isPending}
                />
              </div>

              <div>
                <label
                  htmlFor="bug-description"
                  className="mb-2 block text-[#06141B]/60 dark:text-[#F5F6F5]/60"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, fontWeight: 500 }}
                >
                  Descrição do problema <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="bug-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[120px] w-full resize-none rounded-[10px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#06141B]/20 dark:bg-[#111] dark:text-[#F5F6F5] dark:focus:ring-[#F5F6F5]/20"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 12,
                    color: '#06141B',
                    border: '1px solid rgba(6, 20, 27, 0.15)',
                  }}
                  placeholder="Conte o que você estava fazendo, o que esperava que acontecesse e o que aconteceu de fato."
                  disabled={mutation.isPending}
                />
              </div>

              <div>
                <label
                  htmlFor="bug-screenshot"
                  className="mb-2 block text-[#06141B]/60 dark:text-[#F5F6F5]/60"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, fontWeight: 500 }}
                >
                  Captura de tela (opcional)
                </label>
                <input
                  id="bug-screenshot"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    setScreenshot(file);
                  }}
                  className="block w-full text-[12px] text-[#06141B] file:mr-3 file:rounded-[10px] file:border-0 file:bg-[#F9FAFB] file:px-4 file:py-2 file:font-medium file:text-[#2D3748] dark:text-[#F5F6F5] dark:file:bg-white/10 dark:file:text-[#F5F6F5]"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                  }}
                  disabled={mutation.isPending}
                />
                <p
                  className="mt-1.5 text-[#06141B]/55 dark:text-[#F5F6F5]/55"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: 11, lineHeight: 1.4 }}
                >
                  Dica: tire um print da tela mostrando o erro ou comportamento inesperado.
                </p>
              </div>

              {error && (
                <p className="text-sm text-red-600 dark:text-red-400" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {error}
                </p>
              )}
            </div>
          </div>

          <div
            className="flex flex-shrink-0 justify-end gap-3 px-5 py-4"
            style={{ borderTop: '1px solid rgba(6, 20, 27, 0.08)' }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="rounded-[10px] px-4 py-2 transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                backgroundColor: '#F9FAFB',
                color: '#2D3748',
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !description.trim()}
              className="rounded-[10px] px-4 py-2 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                backgroundColor: '#06141B',
                color: '#F9FAFB',
              }}
            >
              {mutation.isPending ? 'Aguarde...' : 'Enviar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
